import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { GeoJsonProperties, Geometry, Feature } from 'geojson';
import { useNavigate, useParams } from 'react-router-dom';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CircularProgress from '@mui/material/CircularProgress';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import moment from 'moment-timezone';
import GenericIcons from '../../components/generics/GenericIcons';
import LineChart from '../../components/LineChart';
import EstudioConexionSnapshotBoxplots, {
  ecgExtractBoxplotSnapshot
} from '../../components/estudioConexion/EstudioConexionSnapshotBoxplots';
import EstudioConexionTensionDiariaChart from '../../components/estudioConexion/EstudioConexionTensionDiariaChart';
import EstudioConexionResumenProyectoCard from '../../components/estudioConexion/EstudioConexionResumenProyectoCard';
import MapaImpacto, { EscenarioMapa } from '../../components/estudioConexion/MapaImpacto';
import MapComponent from '../../components/maps/Map';
import { SimpleObject } from '../../types';
import { getUncatchEndpointData, sendFileWithData } from '../../utils/request';
import {
  BadgeAdicional,
  BloqueDetallePerdidas,
  BloqueTablaComparativaDiario,
  BloqueTablaComparativaSnapshot,
  BloqueVeredictoDual,
  ETIQUETAS_SNAPSHOT_CUATRO,
  EcgBandasTension,
  escenariosDiariosConProyecto,
  escenariosSnapshotConProyecto,
  extractBandasTensionArtefacto,
  parseResultadoTecnico
} from '../../components/estudioConexion/estudioConexionResultadoTecnicoShared';
import '../../css/PrivateEstudioConexion.css';

const POLL_INTERVAL = 180000;

const isTerminal = (estadoId: number) => estadoId === 2 || estadoId === 3;

const PrivateEstudioConexionResultPage: FunctionComponent = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [estudio, setEstudio] = useState<SimpleObject | null>(null);
  const [resultados, setResultados] = useState<SimpleObject | null>(null);
  const [loading, setLoading] = useState(true);
  const [tecnicoTab, setTecnicoTab] = useState(0);
  const [escenarioDiario, setEscenarioDiario] = useState('');
  const [escenarioSnapshot, setEscenarioSnapshot] = useState('');
  const [selectedEscenario, setSelectedEscenario] = useState('');
  const [mapaEscenario, setMapaEscenario] = useState<EscenarioMapa>('snapshot_dmin');
  const [detalleData, setDetalleData] = useState<SimpleObject[] | null>(null);
  const [loadingMap, setLoadingMap] = useState(false);
  const [mapState, setMapState] = useState({
    mapPointFeatures: [] as Feature<Geometry, GeoJsonProperties>[],
    mapLineFeatures: [] as Feature<Geometry, GeoJsonProperties>[],
    mapPolygonFeatures: [] as Feature<Geometry, GeoJsonProperties>[],
    electricFeeders: {} as SimpleObject,
    mapCenter: {} as { lat: number; lng: number }
  });

  const MT_ACTIVE = '#e06c00';
  const MT_INACTIVE = '#f0c8a0';
  const BT_ACTIVE = '#0072b2';
  const BT_INACTIVE = '#a0cfe0';
  const UNKNOWN_COLOR = '#c8c8c8';

  useEffect(() => {
    setTecnicoTab(0);
    setEscenarioDiario('');
    setEscenarioSnapshot('');
    setSelectedEscenario('');
  }, [id]);

  const fetchEstudio = useCallback(async () => {
    try {
      const data = await getUncatchEndpointData({
        endpoint: `estudios-conexion/${id}`
      });
      setEstudio(data);
      return data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [id]);

  const fetchResultados = useCallback(async () => {
    try {
      const data = await getUncatchEndpointData({
        endpoint: `estudios-conexion/${id}/resultados-base`
      });
      setResultados(data);
    } catch {
      setResultados(null);
    }
  }, [id]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const est = await fetchEstudio();
      if (est && isTerminal(est.estadoSimulacionId)) {
        await fetchResultados();
      }
      setLoading(false);
    };
    init();
  }, [fetchEstudio, fetchResultados]);

  // Polling
  useEffect(() => {
    if (estudio?.estadoSimulacionId == null || isTerminal(estudio.estadoSimulacionId)) return;

    const interval = setInterval(async () => {
      const est = await fetchEstudio();
      if (est && isTerminal(est.estadoSimulacionId)) {
        await fetchResultados();
      }
    }, POLL_INTERVAL);

    const handleVisibility = async () => {
      if (document.visibilityState === 'visible') {
        const est = await fetchEstudio();
        if (est && isTerminal(est.estadoSimulacionId)) {
          await fetchResultados();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [estudio?.estadoSimulacionId, fetchEstudio, fetchResultados]);

  // Detalle por escenario
  useEffect(() => {
    if (!selectedEscenario || !id) return;
    const fetchDetalle = async () => {
      try {
        const data = await getUncatchEndpointData({
          endpoint: `estudios-conexion/${id}/detalle/${selectedEscenario}`
        });
        setDetalleData(data);
      } catch {
        setDetalleData(null);
      }
    };
    fetchDetalle();
  }, [id, selectedEscenario]);

  const fetchMapPoints = useCallback(async (alimentadorId: number, posteId: number | null, nivel: string | null) => {
    if (!alimentadorId) return;
    setLoadingMap(true);
    try {
      const res = await getUncatchEndpointData({
        endpoint: 'mapElements/geoJSON/all',
        query: { alimentadorId }
      });
      const pointFeatures = (res?.pointFeatures || []) as Feature<Geometry, GeoJsonProperties>[];
      const lineFeatures = (res?.lineFeatures || []) as Feature<Geometry, GeoJsonProperties>[];
      const polygonFeatures = (res?.polygonFeatures || []) as Feature<Geometry, GeoJsonProperties>[];
      const alimentadores = res?.alimentadores || {};

      let center = { lat: 0, lng: 0 };
      const selectedPoint = pointFeatures.find((feature) => {
        const featureId = Number(feature.properties?.id ?? feature.properties?.poste_id);
        return posteId != null && !Number.isNaN(featureId) && featureId === posteId;
      });

      if (selectedPoint && selectedPoint.geometry?.type === 'Point') {
        const [lng, lat] = selectedPoint.geometry.coordinates;
        center = { lat, lng };
      } else if (pointFeatures.length > 1 && pointFeatures[1].geometry?.type === 'Point') {
        center = {
          lng: pointFeatures[1].geometry.coordinates[0],
          lat: pointFeatures[1].geometry.coordinates[1]
        };
      } else if (lineFeatures.length > 1 && lineFeatures[1].geometry?.type === 'LineString') {
        center = {
          lng: lineFeatures[1].geometry.coordinates[0][0],
          lat: lineFeatures[1].geometry.coordinates[0][1]
        };
      }

      const styledLines = lineFeatures.map((feature) => {
        const tensionKv = feature.properties?.descripcionArtefacto?.tension_kv;
        if (tensionKv == null) {
          return { ...feature, properties: { ...feature.properties, color: UNKNOWN_COLOR, lineWidth: 1.5 } };
        }
        const isMt = tensionKv > 1;
        const selected = nivel === 'mt' ? isMt : nivel === 'bt' ? !isMt : true;
        const color = isMt
          ? (selected ? MT_ACTIVE : MT_INACTIVE)
          : (selected ? BT_ACTIVE : BT_INACTIVE);
        return {
          ...feature,
          properties: { ...feature.properties, color, lineWidth: selected ? 4 : 2 }
        };
      });

      const styledPoints = pointFeatures.map((feature) => {
        const featureId = Number(feature.properties?.id ?? feature.properties?.poste_id);
        const isSelected = posteId != null && !Number.isNaN(featureId) && featureId === posteId;
        if (!isSelected) return feature;
        return {
          ...feature,
          properties: { ...feature.properties, highlighted: true }
        };
      });

      setMapState({
        mapPointFeatures: styledPoints,
        mapLineFeatures: styledLines,
        mapPolygonFeatures: polygonFeatures,
        electricFeeders: alimentadores,
        mapCenter: center
      });
    } catch (error) {
      console.error(error);
    }
    setLoadingMap(false);
  }, []);

  useEffect(() => {
    if (!estudio?.alimentadorId) return;
    fetchMapPoints(
      Number(estudio.alimentadorId),
      estudio.posteId != null ? Number(estudio.posteId) : null,
      (estudio.nivelTension as string) || null
    );
  }, [estudio?.alimentadorId, estudio?.posteId, estudio?.nivelTension, fetchMapPoints]);

  const resultadoTecnicoData = resultados?.resultadoJson as SimpleObject | undefined;

  const bandasTension = extractBandasTensionArtefacto(resultadoTecnicoData);

  useEffect(() => {
    if (!resultadoTecnicoData) return;
    const rt = parseResultadoTecnico(resultadoTecnicoData);
    const diarios = escenariosDiariosConProyecto(rt);
    const snaps = escenariosSnapshotConProyecto(rt);
    const diarioOk = diarios.some((d) => d.value === escenarioDiario);
    const snapOk = snaps.some((d) => d.value === escenarioSnapshot);
    if (diarios.length === 0) {
      if (escenarioDiario !== '') setEscenarioDiario('');
    } else if (!diarioOk) {
      setEscenarioDiario(diarios[0].value);
    }
    if (snaps.length === 0) {
      if (escenarioSnapshot !== '') setEscenarioSnapshot('');
    } else if (!snapOk) {
      setEscenarioSnapshot(snaps[0].value);
    }
  }, [resultadoTecnicoData, escenarioDiario, escenarioSnapshot]);

  const exportPDF = async () => {
    const el = document.getElementById('ecg-result-content');
    if (!el) return;
    const canvas = await html2canvas(el, { scale: 2, useCORS: true });
    const pdf = new jsPDF('p', 'mm', 'a4');
    pdf.setFillColor('#f9f9f9');
    pdf.rect(0, 0, 210, 297, 'F');
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 10, 190, 277);
    const currentDate = moment().format('DD-MM-YYYY');
    const fileName = `Estudio_ConexionGD_${estudio?.nombre || id}_${currentDate}.pdf`;
    const pdfBase64 = pdf.output('datauristring');
    try {
      await sendFileWithData({
        endpoint: 'estudios-conexion/' + id + '/exportar-pdf',
        dataObject: { fileName, fileContent: pdfBase64 }
      });
    } catch (err) {
      console.error(err);
    }
    pdf.save(fileName);
  };

  if (loading) {
    return (
      <div className='ecg-page-container' style={{ alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  if (!estudio) {
    return (
      <div className='ecg-blocked-container'>
        <h2 className='ecg-blocked-title'>Estudio no encontrado</h2>
        <button className='secondary-button-style vdr-ecg-back' onClick={() => navigate('/private/estudios')}>
          Volver a Mis estudios
        </button>
      </div>
    );
  }

  const estadoId = estudio.estadoSimulacionId;
  const estadoTexto = estudio.estadoSimulacionTexto || estudio.estado;

  return (
    <div className='ecg-page-container' id='ecg-result-content'>
      <div className='ecg-title-container vdr-page-head'>
        <div className='vdr-ecg-heading vdr-page-head__titles'>
          <span className='vdr-eyebrow vdr-eyebrow--bar'>
            Estudio de conexión GD · Estudio base · N.º {id}
          </span>
          <h2 className='ecg-title vdr-page-head__title'>Resultados — {estudio.nombre}</h2>
        </div>
        <div className='vdr-ecg-actions vdr-page-head__actions'>
          <button className='primary-button-style hidden-to-export' onClick={exportPDF}>
            <GenericIcons icon='filePdfIcon' imageStyle={{ height: '1rem' }} />
            Exportar PDF
          </button>
          <button
            className='secondary-button-style vdr-ecg-back hidden-to-export'
            onClick={() => navigate('/private/estudios')}>
            Volver
          </button>
        </div>
      </div>

      <EstudioConexionResumenProyectoCard estudio={estudio} showEstadoSimulacion />

      <div className='ecg-section'>
        <h3 className='ecg-section-title'>Ubicación del Proyecto</h3>
        <p className='vdr-ecg-lead'>
          El mapa muestra el poste y punto de conexión seleccionado para este estudio.
        </p>
        <div className='ecg-map-container' style={{ height: 460 }}>
          <MapComponent
            mapPointFeatures={mapState.mapPointFeatures}
            mapLineFeatures={mapState.mapLineFeatures}
            mapPolygonFeatures={mapState.mapPolygonFeatures}
            electricFeeders={mapState.electricFeeders}
            mapCenter={mapState.mapCenter}
            mapZoom={16.5}
          />
          {loadingMap && (
            <div className='ecg-map-loader'>
              <CircularProgress />
            </div>
          )}
        </div>
      </div>

      {/* En progreso / En espera */}
      {!isTerminal(estadoId) && (
        <div className='ecg-section vdr-ecg-status-panel'>
          <CircularProgress />
          <p className='vdr-ecg-status-text'>
            El estudio se encuentra <strong>{estadoTexto?.toLowerCase()}</strong>. Esta página se
            actualiza automáticamente cada 3 minutos. Recibirá un correo cuando finalice.
          </p>
        </div>
      )}

      {/* Error */}
      {estadoId === 3 && (
        <div className='ecg-section vdr-ecg-status-panel'>
          <p className='vdr-ecg-status-error'>
            El estudio finalizó con un error. Si el problema persiste, contacte al administrador.
          </p>
        </div>
      )}

      {/* Resultados — bloque 1: expeditivo */}
      {estadoId === 2 && resultados && (
        <div className='ecg-section'>
          <h3 className='ecg-section-title'>Resumen expeditivo</h3>
          {resultados.resultadoExpeditivoJson ? (
            <TabExpeditivo data={resultados.resultadoExpeditivoJson} />
          ) : (
            <p className='ecg-hint'>No hay resultado expeditivo disponible.</p>
          )}
        </div>
      )}

      {/* Bloque 2: estudios técnicos detallados */}
      {estadoId === 2 && resultados && resultadoTecnicoData && (
        <div className='ecg-section ecg-tabs-container'>
          <h3 className='ecg-section-title'>Estudios técnicos detallados</h3>
          <p className='vdr-ecg-lead'>
            Comparación red <strong>sin el proyecto</strong> frente a red <strong>con el proyecto</strong>{' '}
            según los criterios del estudio (instantáneos, pérdidas cuando aplican y cortocircuito;
            el flujo diario se reporta como información adicional).
          </p>
          <Tabs value={tecnicoTab} onChange={(_, v) => setTecnicoTab(v)} sx={{ mb: 2 }}>
            <Tab label='Resumen de impacto' />
            <Tab label='Detalle — snapshots' />
            <Tab label='Detalle — pérdidas' />
            <Tab label={
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                Detalle — flujo diario<BadgeAdicional small />
              </span>
            } />
          </Tabs>
          {tecnicoTab === 0 && (
            <BloqueVeredictoDual data={resultadoTecnicoData} />
          )}
          {tecnicoTab === 1 && (
            <BloqueTablaComparativaSnapshot
              data={resultadoTecnicoData}
              escenarioSeleccionado={escenarioSnapshot}
              onCambioEscenario={setEscenarioSnapshot}
            />
          )}
          {tecnicoTab === 2 && (
            <BloqueDetallePerdidas data={resultadoTecnicoData} />
          )}
          {tecnicoTab === 3 && (
            <div>
              <p className='ecg-hint' style={{ marginBottom: '0.75rem' }}>
                <strong>Información adicional.</strong> El flujo horario diario es un análisis
                complementario del producto; no determina por sí solo la decisión de conexión.
              </p>
              <BloqueTablaComparativaDiario
                data={resultadoTecnicoData}
                escenarioSeleccionado={escenarioDiario}
                onCambioEscenario={setEscenarioDiario}
              />
            </div>
          )}
        </div>
      )}

      {/* Bloque 3: gráficos (igual que antes, fuera de tabs principales) */}
      {estadoId === 2 && resultados && (
        <div className='ecg-section'>
          <h3 className='ecg-section-title'>Análisis gráfico por escenario</h3>
          <p className='vdr-ecg-lead'>
            Seleccione un escenario para cargar series de tiempo o vista de snapshot desde el detalle del estudio.
          </p>
          <TabDetalle
            escenario={selectedEscenario}
            setEscenario={setSelectedEscenario}
            detalleData={detalleData}
            bandasTension={bandasTension}
          />
        </div>
      )}

      {/* Bloque 4: Mapa de impacto — bloque independiente con selector propio
          de 4 opciones (snapshots normativos + flujos diarios). Desacoplado del
          selector de boxplots. */}
      {estadoId === 2 && resultados && id && (
        <div className='ecg-section'>
          <h3 className='ecg-section-title'>Mapa de impacto del proyecto</h3>
          <p className='vdr-ecg-lead'>
            Seleccione el escenario sobre el que desea analizar el impacto del proyecto.
          </p>
          <div className='ecg-mapa-impacto-escenario-selector'>
            <label htmlFor='mapa-impacto-escenario' className='ecg-label'>
              Escenario:
            </label>
            <select
              id='mapa-impacto-escenario'
              className='ecg-input'
              value={mapaEscenario}
              onChange={(e) => setMapaEscenario(e.target.value as EscenarioMapa)}>
              <option value='snapshot_dmin'>Snapshot Dmin</option>
              <option value='snapshot_dmax'>Snapshot Dmax</option>
              <option value='dia_verano'>Día Verano</option>
              <option value='dia_invierno'>Día Invierno</option>
            </select>
          </div>
          <MapaImpacto
            estudioId={id}
            escenarioMapa={mapaEscenario}
            mapBaseState={mapState}
          />
        </div>
      )}

      {/* Acciones post-resultado */}
      {estadoId === 2 && (
        <div className='ecg-actions'>
          <button
            className='primary-button-style vdr-ecg-cta'
            onClick={() => navigate(`/private/estudio-conexion/${id}/mitigaciones`)}>
            Evaluar medidas de mitigación
          </button>
        </div>
      )}
    </div>
  );
};

export default PrivateEstudioConexionResultPage;

// ─── Sub-componentes de tabs ────────────────────────────────────────────────

const TabExpeditivo: FunctionComponent<{ data: SimpleObject }> = ({ data }) => {
  const tipoEvaluacion = data.tipo_evaluacion;
  const criterios = data.criterios || {};
  const cumpleGlobal = data.cumple_global === true;
  const potenciaMaxExpeditiva = data.potencia_max_expeditiva_kw;
  const omitido = data.omitido === true;
  const razonOmision = typeof data.razon === 'string' ? data.razon.trim() : '';
  const requiereEstudios = data.requiere_estudios_tecnicos === true;

  const sections =
    tipoEvaluacion === 'EG'
      ? [
          { key: 'restricciones_previas', label: 'Restricciones previas de conexión (CI/CIT)' },
          { key: 'variacion_tension', label: 'Variación de tensión permanente (ΔV)' },
          { key: 'cip', label: 'Capacidad Instalada Permitida (CIP)' },
          { key: 'iep', label: 'Inyección de Excedentes Permitida (IEP)' },
          { key: 'capacidad_ruptura', label: 'Capacidad de ruptura (85%)', soloCumple: true }
        ]
      : [
          { key: 'restricciones_previas', label: 'Límite de potencia expeditiva (≤ 1.500 kW)' },
          { key: 'variacion_tension', label: 'Variación de tensión permanente (ΔV)' },
          {
            key: 'capacidad_cortocircuito',
            label: 'Capacidad Instalada para Conexión Expeditiva'
          },
          {
            key: 'ice',
            label: 'Capacidad de Inyección para Conexión Expeditiva'
          },
          { key: 'capacidad_ruptura', label: 'Capacidad de ruptura (85%)', soloCumple: true }
        ];

  return (
    <div>
      <div className='ecg-form-row' style={{ marginBottom: '1rem' }}>
        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Tipo</span>
          <span className='ecg-info-value'>{tipoEvaluacion || '—'}</span>
        </div>
        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Norma</span>
          <span className='ecg-info-value'>{data.norma || '—'}</span>
        </div>
        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Potencia máxima expeditiva</span>
          <span className='ecg-info-value'>
            {potenciaMaxExpeditiva != null ? `${potenciaMaxExpeditiva} kW` : '—'}
          </span>
        </div>
        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Califica expeditivo</span>
          <span className='ecg-info-value'>
            <span className={`ecg-semaforo-icon ${cumpleGlobal ? 'cumple' : 'no-cumple'}`}
              style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 8 }} />
            {cumpleGlobal ? 'Sí' : 'No'}
          </span>
        </div>
      </div>
      {omitido && (
        <div className='vdr-result-callout'>
          <p className='vdr-result-callout-title'>
            Evaluación expeditiva omitida
          </p>
          {razonOmision && (
            <p className='vdr-result-callout-body'>
              {razonOmision}
            </p>
          )}
          {requiereEstudios && (
            <p className='vdr-result-callout-body' style={{ color: 'var(--vdr-warning)', fontWeight: 600 }}>
              El proyecto pasa directamente a estudios técnicos detallados.
            </p>
          )}
        </div>
      )}
      {!omitido && sections.map(({ key, label, soloCumple }) => {
        const section = criterios[key];
        if (!section) return null;
        const cumple = section.cumple === true;
        const descripcion = soloCumple
          ? (cumple ? 'Cumple' : 'No cumple')
          : (section.descripcion || (cumple ? 'Cumple' : 'No cumple'));

        return (
          <div key={key} className='ecg-semaforo-row'>
            <span className={`ecg-semaforo-icon ${cumple ? 'cumple' : 'no-cumple'}`} />
            <span className='ecg-semaforo-label'>{label}</span>
            <span className='ecg-semaforo-value'>
              {descripcion}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// NORM-002 / PLAN_NORM_002_SNAPSHOTS_DEMANDA_ANUAL §8.5: el motor entrega
// 4 snapshots por estudio con sufijo `_anual` (sin duplicación estacional).
// Este selector se alinea al nuevo contrato; la página detecta estudios
// legacy (sufijos `_verano`/`_invierno`) por los datos devueltos por el API
// y las etiquetas legacy siguen disponibles en `ETIQUETAS_SNAPSHOT_CUATRO`.
const ESCENARIOS = [
  { value: 'dia_verano__con_proyecto', label: 'Día verano (con proyecto)' },
  { value: 'dia_invierno__con_proyecto', label: 'Día invierno (con proyecto)' },
  { value: 'dia_verano__sin_proyecto', label: 'Día verano (sin proyecto)' },
  { value: 'dia_invierno__sin_proyecto', label: 'Día invierno (sin proyecto)' },
  { value: 'snapshot_dmin_sinGD_anual__sin_proyecto', label: ETIQUETAS_SNAPSHOT_CUATRO.snapshot_dmin_sinGD_anual },
  { value: 'snapshot_dmax_sinGD_anual__sin_proyecto', label: ETIQUETAS_SNAPSHOT_CUATRO.snapshot_dmax_sinGD_anual }
];

/** Paleta para curvas p10…p90 en flujo diario (capa B). */
const ECG_DECILE_LINE_COLORS = [
  '#d0d0d0', // p0
  '#b0b0b0', // p2
  '#909090', // p5
  '#1b9e77', // p10
  '#d95f02', // p20
  '#7570b3', // p30
  '#e7298a', // p40
  '#66a61e', // p50
  '#e6ab02', // p60
  '#a6761d', // p70
  '#666666', // p80
  '#01665e', // p90
  '#909090', // p95
  '#b0b0b0', // p98
  '#d0d0d0'  // p100
];

type EcgFlujoSubVista = 'tension' | 'corriente' | 'trafos';

function ecgPickDj<T>(dj: SimpleObject, snake: string, camel: string): T | undefined {
  const a = dj[snake];
  if (a !== undefined && a !== null) return a as T;
  const b = dj[camel];
  if (b !== undefined && b !== null) return b as T;
  return undefined;
}

function ecgInstantesMuestra(dj: SimpleObject): number[] | null {
  const raw = ecgPickDj<unknown>(dj, 'instantes_muestra', 'instantesMuestra');
  if (!Array.isArray(raw) || raw.length === 0) return null;
  return raw.map((x) => Number(x));
}

function ecgIsLegacyDetalleCharts(dj: SimpleObject): boolean {
  const vals = dj.values;
  return Array.isArray(dj.timestamps) && vals != null && typeof vals === 'object' && !Array.isArray(vals);
}

function ecgDecileSeries(bundle: unknown): { label: string; data: number[]; curve: 'linear' }[] {
  if (!bundle || typeof bundle !== 'object') return [];
  const o = bundle as SimpleObject;
  const order = ['p0', 'p2', 'p5', 'p10', 'p20', 'p30', 'p40', 'p50', 'p60', 'p70', 'p80', 'p90', 'p95', 'p98', 'p100'];
  const out: { label: string; data: number[]; curve: 'linear' }[] = [];
  for (const k of order) {
    const arr = o[k];
    if (Array.isArray(arr)) {
      out.push({ label: k.toUpperCase(), data: arr.map((x) => Number(x)), curve: 'linear' });
    }
  }
  return out;
}

function ecgFlujoDecilesBundle(dj: SimpleObject, sub: EcgFlujoSubVista): unknown {
  if (sub === 'tension') {
    return ecgPickDj(dj, 'tensiones_bus_pu_deciles', 'tensionesBusPuDeciles');
  }
  if (sub === 'corriente') {
    return ecgPickDj(dj, 'corrientes_linea_pu_deciles', 'corrientesLineaPuDeciles');
  }
  return ecgPickDj(dj, 'utilizacion_trafos_pu_deciles', 'utilizacionTrafosPuDeciles');
}

/** `instantes_muestra` del motor = minutos desde 00:00 del día simulado (muestreo cada `intervalo_minutos`). */
function ecgMinutesToHHmm(minutesFromMidnight: number): string {
  let m = Math.round(minutesFromMidnight) % (24 * 60);
  if (m < 0) m += 24 * 60;
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** Ticks del eje X cada 30 min (valores en minutos-día); siempre se muestra el último instante. */
function ecgTickIntervalEvery30Min(minutesSeries: number[]) {
  const lastIdx = minutesSeries.length - 1;
  return (value: unknown, index: number) => {
    const m = Number(value);
    if (!Number.isFinite(m)) return false;
    if (index === lastIdx) return true;
    return m % 30 === 0;
  };
}

/** Eje X en escala `point`: etiquetas HH:mm y marcas cada 30 min. */
function ecgTimeOfDayXAxisConfig(xData: number[], dj: SimpleObject) {
  const step = ecgPickDj<number>(dj, 'intervalo_minutos', 'intervaloMinutos');
  const stepLabel =
    step != null && !Number.isNaN(step)
      ? `Hora del día (muestreo cada ${step} min; marcas cada 30 min)`
      : 'Hora del día (marcas cada 30 min)';

  return {
    scaleType: 'point' as const,
    data: xData,
    label: stepLabel,
    valueFormatter: (value: number) => ecgMinutesToHHmm(Number(value)),
    tickInterval: ecgTickIntervalEvery30Min(xData)
  };
}

const TabDetalle: FunctionComponent<{
  escenario: string;
  setEscenario: (v: string) => void;
  detalleData: SimpleObject[] | null;
  bandasTension: EcgBandasTension;
}> = ({ escenario, setEscenario, detalleData, bandasTension }) => {
  const [flujoSubVista, setFlujoSubVista] = useState<EcgFlujoSubVista>('tension');
  const esCortocircuito = escenario.startsWith('cortocircuito');
  const esSnapshot = escenario.startsWith('snapshot_');

  return (
    <div>
      <div className='ecg-form-row'>
        <div className='ecg-form-field' style={{ maxWidth: 400 }}>
          <label className='ecg-label'>Escenario</label>
          <select className='ecg-input' value={escenario} onChange={(e) => setEscenario(e.target.value)}>
            <option value=''>Seleccione un escenario</option>
            {ESCENARIOS.map((e) => (
              <option key={e.value} value={e.value}>{e.label}</option>
            ))}
          </select>
        </div>
      </div>
      {!escenario && <p className='ecg-hint' style={{ marginTop: '0.5rem' }}>Seleccione un escenario para ver el detalle.</p>}
      {escenario && !detalleData && <CircularProgress sx={{ mt: 2 }} />}
      {escenario && detalleData && detalleData.length === 0 && (
        <p className='ecg-hint' style={{ marginTop: '0.5rem' }}>No hay datos disponibles para este escenario.</p>
      )}
      {escenario && esCortocircuito && detalleData && detalleData.length > 0 && (
        <div className='vdr-result-empty'>
          <p>
            El detalle de cortocircuito por elemento no está disponible en este estudio.
          </p>
          {detalleData[0]?.datosJson && (
            <div className='vdr-result-empty-detail'>
              {(() => {
                const ccj = detalleData[0].datosJson as SimpleObject;
                const n85 = ccj.n_elementos_sobre_85pct ?? ccj.nElementosSobre85pct;
                const nU = ccj.n_sobre_umbral ?? ccj.nSobreUmbral;
                const pctU = ccj.pct_sobre_umbral ?? ccj.pctSobreUmbral;
                return (
                  <>
                    <p><strong>Resultado global:</strong> {ccj.cumple ? 'Cumple' : 'No cumple'}</p>
                    {nU != null && <p>Elementos sobre umbral: {nU}</p>}
                    {pctU != null && <p>Porcentaje sobre umbral: {pctU}%</p>}
                    {n85 != null && <p>Elementos sobre 85% capacidad de ruptura: {n85}</p>}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}
      {escenario && esSnapshot && detalleData && detalleData.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          {detalleData.map((d, i) => {
            const tipoRes = String(d.tipoResultado ?? '');
            const dj = d.datosJson as SimpleObject | undefined;
            if (tipoRes === 'flujo_snapshot' && dj && typeof dj === 'object') {
              const bp = ecgExtractBoxplotSnapshot(dj);
              return (
                <div key={i}>
                  {bp ? (
                    <EstudioConexionSnapshotBoxplots boxplotRoot={bp} bandas={bandasTension} />
                  ) : (
                    <p className='ecg-hint' style={{ marginTop: '0.75rem' }}>
                      Los gráficos de distribución no están disponibles para este estudio.
                    </p>
                  )}
                </div>
              );
            }
            return (
              <div key={i} className='ecg-semaforo-row'>
                <span className='ecg-semaforo-label'>{d.tipoResultado}</span>
                <span className='ecg-semaforo-value'>
                  {dj && typeof dj === 'object'
                    ? Object.entries(dj).map(([k, v]) => `${k}: ${v}`).join(' | ')
                    : '—'}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {escenario && !esCortocircuito && !esSnapshot && detalleData && detalleData.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          {detalleData.map((d, i) => {
            const dj = d.datosJson as SimpleObject | undefined;
            if (!dj || typeof dj !== 'object') return null;

            if (ecgIsLegacyDetalleCharts(dj)) {
              const series = Object.keys(dj.values as SimpleObject).map((k) => ({
                label: k.replace(/_/g, ' '),
                curve: 'linear' as const,
                data: (dj.values as SimpleObject)[k] as number[]
              }));
              return (
                <div key={i} style={{ marginBottom: '1.5rem' }}>
                  <h4 className='vdr-result-chart-title'>
                    {String(d.tipoResultado || '').replace(/_/g, ' ')} (formato legado)
                  </h4>
                  <LineChart
                    height={350}
                    xAxis={[{ data: dj.timestamps as number[], label: 'Tiempo' }]}
                    yAxis={[{ label: String(d.tipoResultado || '') }]}
                    series={series}
                  />
                </div>
              );
            }

            const tipo = String(d.tipoResultado || '');
            const xData = ecgInstantesMuestra(dj);
            const xAxisHoraDelDia =
              xData != null ? ecgTimeOfDayXAxisConfig(xData, dj) : null;

            if (tipo === 'flujo_dia') {
              const bundle = ecgFlujoDecilesBundle(dj, flujoSubVista);
              const series = ecgDecileSeries(bundle);
              const yLabel =
                flujoSubVista === 'corriente'
                  ? 'Corriente líneas (pu, deciles)'
                  : 'Utilización trafos (pu, deciles)';
              return (
                <div key={i} style={{ marginBottom: '1.5rem' }}>
                  <h4 className='vdr-result-chart-title'>
                    {tipo.replace(/_/g, ' ')}
                  </h4>
                  <div className='ecg-form-row' style={{ marginBottom: '0.5rem' }}>
                    <div className='ecg-form-field' style={{ maxWidth: 320 }}>
                      <label className='ecg-label'>Magnitud</label>
                      <select
                        className='ecg-input'
                        value={flujoSubVista}
                        onChange={(e) => setFlujoSubVista(e.target.value as EcgFlujoSubVista)}>
                        <option value='tension'>Tensión en buses (deciles)</option>
                        <option value='corriente'>Corriente en líneas (deciles)</option>
                        <option value='trafos'>Utilización transformadores (deciles)</option>
                      </select>
                    </div>
                  </div>
                  {flujoSubVista === 'tension' ? (
                    <EstudioConexionTensionDiariaChart
                      datosJson={dj}
                      bandas={bandasTension}
                      height={380}
                    />
                  ) : !xData ? (
                    <p className='ecg-hint'>Sin eje temporal (instantes_muestra) para este bloque.</p>
                  ) : series.length === 0 ? (
                    <p className='ecg-hint'>No hay series de deciles para este escenario.</p>
                  ) : (
                    <LineChart
                      height={380}
                      xAxis={xAxisHoraDelDia ? [xAxisHoraDelDia] : []}
                      yAxis={[{ label: yLabel }]}
                      series={series}
                      colors={ECG_DECILE_LINE_COLORS}
                    />
                  )}
                </div>
              );
            }

            if (!xData) {
              return (
                <div key={i} className='ecg-hint' style={{ marginBottom: '1rem' }}>
                  <strong>{tipo.replace(/_/g, ' ')}</strong>: sin datos de eje temporal para este escenario.
                </div>
              );
            }

            if (tipo === 'perdidas') {
              const kw = ecgPickDj<number[]>(dj, 'losses_kW', 'lossesKW');
              const pct = ecgPickDj<number[]>(dj, 'losses_pct', 'lossesPct');
              if (!kw || !pct) return null;
              return (
                <div key={i} style={{ marginBottom: '1.5rem' }}>
                  <h4 className='vdr-result-chart-title'>
                    {tipo.replace(/_/g, ' ')}
                  </h4>
                  <LineChart
                    height={360}
                    xAxis={xAxisHoraDelDia ? [xAxisHoraDelDia] : []}
                    yAxis={[
                      { id: 'kw', label: 'Pérdidas (kW)' },
                      { id: 'pct', label: 'Pérdidas (%)' }
                    ]}
                    series={[
                      { curve: 'linear', data: kw, label: 'kW', yAxisId: 'kw' },
                      { curve: 'linear', data: pct, label: '%', yAxisId: 'pct' }
                    ]}
                  />
                </div>
              );
            }

            if (tipo === 'potencia_gd') {
              const pg = ecgPickDj<number[]>(dj, 'potencia_gd_kw', 'potenciaGdKw');
              const pc = ecgPickDj<number[]>(dj, 'potencia_cabecera_kw', 'potenciaCabeceraKw');
              if (!pg || !pc) return null;
              return (
                <div key={i} style={{ marginBottom: '1.5rem' }}>
                  <h4 className='vdr-result-chart-title'>
                    {tipo.replace(/_/g, ' ')}
                  </h4>
                  <LineChart
                    height={350}
                    xAxis={xAxisHoraDelDia ? [xAxisHoraDelDia] : []}
                    yAxis={[{ label: 'Potencia (kW)' }]}
                    series={[
                      { curve: 'linear', data: pg, label: 'GD (kW)' },
                      { curve: 'linear', data: pc, label: 'Cabecera (kW)' }
                    ]}
                  />
                </div>
              );
            }

            if (tipo === 'bateria') {
              const pbat = ecgPickDj<number[]>(dj, 'potencia_kw', 'potenciaKw');
              const soc = ecgPickDj<number[]>(dj, 'soc_pct', 'socPct');
              if (!pbat || !soc) return null;
              return (
                <div key={i} style={{ marginBottom: '1.5rem' }}>
                  <h4 className='vdr-result-chart-title'>
                    {tipo.replace(/_/g, ' ')}
                  </h4>
                  <LineChart
                    height={360}
                    xAxis={xAxisHoraDelDia ? [xAxisHoraDelDia] : []}
                    yAxis={[
                      { id: 'p', label: 'Potencia batería (kW)' },
                      { id: 'soc', label: 'SoC (%)' }
                    ]}
                    series={[
                      { curve: 'linear', data: pbat, label: 'kW', yAxisId: 'p' },
                      { curve: 'linear', data: soc, label: 'SoC %', yAxisId: 'soc' }
                    ]}
                  />
                </div>
              );
            }

            return null;
          })}
        </div>
      )}
    </div>
  );
};
